import React, { useEffect, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { assetTypeOption, assetTypeRealEstateOption, assetTypeStockOption, companyAssetTypeOption, currencyOption, owernershipTypeOption, sectorss } from '../../../../utility/data';
import { InputField, InputFieldWithOutBorder, NumberField, NumberFieldBorder } from '../../../../components/Stepper/fields/inputField';
import { Dropdown } from 'primereact/dropdown';
import { formatNumber, limitToTwoDecimalPlaces, preventNegativeValue } from '../../../../utility/helper';
import { useSelector } from 'react-redux';
import { getSymbolServices } from '../../../../services/addWealth/assetApi';

const AssetForm = ({ assetType, onSubmit, groupsData, userAssets, setLoading, loading }) => {
  const assetTypeMapping = {
    "Stock": assetTypeStockOption[0].value,
    "Real Estate": assetTypeRealEstateOption[0].value,
    "Luxury Asset": assetTypeOption[2].value,
    "Company": assetTypeOption[1].value,
  };
  const { currency } = useSelector((state) => state.user);
  const [sectors, setSectors] = useState(sectorss)
  const [assetData, setAssetData] = useState({
    isin: '',
    name: '',
    inclusion: groupsData && groupsData[0],
    assetType: assetTypeMapping[assetType.name] || assetTypeOption[0].value,
    currency: currency ? currency : currencyOption[0].value,
    sector: assetType.name === "Stock" ? '' : assetType.name === "Real Estate" ? sectors[5].value : assetType.name === "Luxury Asset" ? sectors[1].value : sectors[0].value,
    linkedWith: '',
    ownership: '',
    ownershipType: assetType.name === "Company" ? owernershipTypeOption[0].value : '',
    commitment: '',
    initialBalance: '',
    quantity: '',
    costPrice: '',
    noi: '',
    capRate: '',
    ebitda: '',
    multiples: '',
    date: new Date(),
    assetTypeId: assetType.id,
    symbol: '',
    cashAssets: '',
    companyAssetType: assetType.name === "Company" ? companyAssetTypeOption[0].value : '',
  });
  const [errors, setErrors] = useState({});
  const [stockSymbol, setStockSymbol] = useState([]);
  const [linkedWith, setLinkedWith] = useState([]);

  const accessToken = JSON.parse(localStorage.getItem('access_token'));


  useEffect(() => {
    if (currency && currency !== assetData.currency) {
      setAssetData((prevData) => ({
        ...prevData,
        currency,
      }));
    }
  }, [currency]);


  useEffect(() => {
    if (assetType.name === "Stock" && assetData.isin) {
      const handler = setTimeout(async () => {
        await GetStockSymbolData(assetData.isin);
      }, 200);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [assetData.isin, assetType.name]);

  const getSectorValue = (sectorName) => {
    const sector = sectors.find((s) => s.name === sectorName);

    if (!sector) {
      const newSector = { name: sectorName, value: sectorName };
      setSectors((prevSectors) => [...prevSectors, newSector]);
      return newSector.value;
    }

    return sector.value;
  };

  useEffect(() => {
    if (assetType.name === "Stock" && stockSymbol.length > 0) {
      const sectorValue = getSectorValue(stockSymbol[0].sector);

      setErrors((prevErrors) => ({
        ...prevErrors,
        name: "",
        symbol: "",
        sector: "",
      }));

      setAssetData((prevData) => ({
        ...prevData,
        name: stockSymbol[0]?.companyName,
        symbol: stockSymbol[0] ? stockSymbol[0] : "",
        sector: sectorValue,
      }));
    }
  }, [stockSymbol, assetType.name]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (stockSymbol.length === 0 && assetType.name === "Stock" && !assetData.isin) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          isin: "",
        }));
      } else if (assetType.name === "Stock" && !assetData.isin) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          isin: "Please enter the ISIN.",
        }));
      } else if (stockSymbol.length === 0 && assetType.name === "Stock" && assetData.isin) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          isin: "No data found for this ISIN.",
        }));
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          isin: "",
        }));
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [stockSymbol, assetType, assetData]);


  const GetStockSymbolData = async (searchterms) => {
    try {
      const response = await getSymbolServices(searchterms, accessToken);
      if (response.status === 200) {
        const data = response?.data.data;

        setStockSymbol(data);
      } else {
        throw new Error('Failed to fetch user data');
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, fieldName) => {
    let name, value;

    if (fieldName) {
      name = fieldName;
      value = e;
    } else if (e.target) {
      name = e.target.name;
      value = e.target.value;
    }
    if ((name === 'quantity' || name === 'costPrice') && !preventNegativeValue(value)) {
      return;
    }

    if (name === 'initialBalance' && !limitToTwoDecimalPlaces(value)) {
      return;
    }

    setAssetData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
    }
  };

  const handleNumericChange = (e, fieldName) => {
    const rawValue = e.target.value.replace(/[,']/g, '');

    if (!limitToTwoDecimalPlaces(rawValue)) return;

    setAssetData((prevData) => ({
      ...prevData,
      [fieldName]: formatNumber(rawValue),
    }));

    if (errors[fieldName]) {
      setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: '' }));
    }
  };

  const validateFields = () => {
    let newErrors = {};
    if (assetType.name === 'Stock' && !assetData.isin) newErrors.isin = 'Please enter the ISIN.';
    if (!assetData.name) newErrors.name = 'Enter your asset name.';
    if (!assetData.inclusion) newErrors.inclusion = 'Inclusion details are required';
    if (!assetData.assetType) newErrors.assetType = 'Please select the asset type.';
    if (!assetData.currency) newErrors.currency = 'Please enter the currency.';
    if (!assetData.sector) newErrors.sector = 'Please select a sector.';
    if (assetType.name === 'Company' && !assetData.ownership) newErrors.ownership = 'Please enter the ownership.';
    if (assetType.name === 'Other' && !assetData.commitment) newErrors.commitment = 'Please enter the commitment.';
    if (!assetData.date && assetType.name !== 'Luxury Asset') newErrors.date = 'Please select the date.';
    if (assetType.name == 'Stock' && !assetData.symbol) newErrors.symbol = 'Please select the symbol.';
    if (assetType.name !== 'Stock' && assetType.name !== 'Luxury Asset' && !assetData.initialBalance) newErrors.initialBalance = assetType.name == 'Real Estate' ? "Please enter the Initial Purchase" : "Please enter the initial balance.";
    if (assetType.name === 'Stock' && !assetData.quantity) newErrors.quantity = 'Please enter the quantity.';
    if (assetType.name === 'Stock' && !assetData.costPrice) newErrors.costPrice = 'Please enter the cost price.';
    if (assetType.name === 'Real Estate' && !assetData.noi) newErrors.noi = 'Please enter the NOI.';
    if (assetType.name === 'Real Estate' && !assetData.capRate) newErrors.capRate = 'Please enter the cap rate.';
    if (assetType.name === 'Company' && !assetData.ebitda) newErrors.ebitda = 'Please enter the EBITDA.';
    if (assetType.name === 'Company' && !assetData.multiples) newErrors.multiples = 'Please enter the multiples.';
    if (assetType.name === 'Company' && !assetData.companyAssetType) newErrors.companyAssetType = 'Please select the company Asset Type.';
    if (assetType.name === 'Company' && !assetData.ownershipType) newErrors.ownershipType = 'Please select the ownership Type.';

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateFields();
    if (Object.keys(newErrors).length === 0) {
      onSubmit(assetData, assetType.name);
    } else {
      setErrors(newErrors);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6 text-secondary">General</h2>
      <form onSubmit={handleSubmit}>
        <div className="grid md:grid-cols-2 gap-5 text-primary mb-8">
          {assetType.name === 'Stock' && (
            <>
              <InputField
                label="ISIN*"
                placeholder="Enter your ISIN"
                name="isin"
                value={assetData.isin}
                onChange={handleInputChange}
                required={false}
                errors={errors.isin}
              />
            </>
          )}
          <InputField
            label="Name*"
            placeholder="Enter your asset's name"
            name="name"
            value={assetData.name}
            onChange={handleInputChange}
            required={false}
            errors={errors.name}
          />


          <div className="w-full">
            <label className="block mb-2 text-lg text-white font-semibold">Include In*</label>
            <Dropdown
              value={assetData.inclusion}
              name="inclusion"
              onChange={handleInputChange}
              options={groupsData}
              optionLabel="name"
              placeholder="Select include In"
              className={`w-full bg-transparent border border-gray-300 rounded-lg ${assetData.inclusion ? "asset_Dropdown" : ""}`}

            />
            {errors.inclusion && <p className="text-red-500">{errors.inclusion}</p>}
          </div>


          <div className="w-full">
            <label className="block mb-2 text-lg text-white font-semibold">Asset Type*</label>
            <Dropdown
              value={assetData.assetType}
              name="assetType"
              onChange={handleInputChange}
              options={assetType.name === "Stock" ? assetTypeStockOption : assetType.name === "Real Estate" ? assetTypeRealEstateOption : assetTypeOption}
              optionLabel="name"
              placeholder="Select asset type"
              className={`w-full bg-transparent border border-gray-300 rounded-lg ${assetData.assetType ? "asset_Dropdown" : ""}`}
            />
            {errors.assetType && <p className="text-red-500">{errors.assetType}</p>}
          </div>


          <div className="w-full">
            <label className="block mb-2 text-lg text-white font-semibold">Currency*</label>
            <Dropdown
              value={assetData.currency}
              name="currency"
              onChange={handleInputChange}
              options={currencyOption}
              optionLabel="label"
              placeholder="Select a currency"
              className={`w-full bg-transparent border border-gray-300 rounded-lg ${assetData.currency ? "asset_Dropdown" : ""}`}

            />
            {errors.currency && <p className="text-red-500">{errors.currency}</p>}
          </div>


          <div className="w-full">
            <label className="block mb-2 text-lg text-white font-semibold">Sector*</label>
            <Dropdown
              value={assetData.sector}
              name="sector"
              onChange={handleInputChange}
              options={sectors}
              optionLabel="name"
              placeholder="Select a sector"
              className={`w-full bg-transparent border border-gray-300 rounded-lg ${assetData.sector ? "asset_Dropdown" : ""}`}
            />
            {errors.sector && <p className="text-red-500">{errors.sector}</p>}
          </div>

          {assetType.name === 'Real Estate' && (
            <div className="w-full">
              <label className="block mb-2 text-lg text-white font-semibold">Linked With</label>
              <Dropdown
                value={assetData.linkedWith}
                showClear
                name="linkedWith"
                onChange={handleInputChange}
                options={linkedWith}
                optionLabel="name"
                placeholder="Select a linked With"
                className={`w-full bg-transparent border border-gray-300 rounded-lg ${assetData.linkedWith ? "asset_Dropdown" : ""}`}
              />
            </div>
          )}

          {assetType.name === 'Company' && (
            <>
              <div className="w-full">
                <label className="block mb-2 text-lg text-white font-semibold">Company Asset Type*</label>
                <Dropdown
                  value={assetData.companyAssetType}
                  name="companyAssetType"
                  onChange={handleInputChange}
                  options={companyAssetTypeOption}
                  optionLabel="name"
                  placeholder="Select a Company Asset Type"
                  className={`w-full bg-transparent border border-gray-300 rounded-lg ${assetData.companyAssetType ? "asset_Dropdown" : ""}`}
                />
                {errors.companyAssetType && <p className="text-red-500">{errors.companyAssetType}</p>}
              </div>
              <NumberFieldBorder
                label="Ownership"
                placeholder="Enter your Ownership"
                name="ownership"
                value={assetData.ownership}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue === "" || (Number(newValue) >= 0 && Number(newValue) <= 100)) {
                    handleInputChange(e);
                  }
                }}
                required={false}
                errors={errors.ownership}
              />
              <div className="w-full">
                <label className="block mb-2 text-lg text-white font-semibold">Ownership Type*</label>
                <Dropdown
                  value={assetData.ownershipType}
                  name="ownershipType"
                  onChange={handleInputChange}
                  options={owernershipTypeOption}
                  optionLabel="name"
                  placeholder="Select a Ownership Type"
                  className={`w-full bg-transparent border border-gray-300 rounded-lg ${assetData.ownershipType ? "asset_Dropdown" : ""}`}
                />
                {errors.ownershipType && <p className="text-red-500">{errors.ownershipType}</p>}
              </div>
            </>
          )}

          {assetType.name === 'Other' && (
            <>
              <NumberFieldBorder
                label="Commitment"
                placeholder="Enter Commitment"
                name="commitment"
                value={assetData.commitment}
                onChange={handleInputChange}
                required={false}
                errors={errors.commitment}
              />

            </>
          )}
          {assetType.name == 'Stock' && (
            <>
              <div className="w-full">
                <label className="block mb-2 text-lg text-white font-semibold">Symbol*</label>
                <Dropdown
                  value={assetData.symbol}
                  name="symbol"
                  onChange={handleInputChange}
                  options={stockSymbol}
                  optionLabel={(option) => `${option.companyName} - ${option.exchangeShortName}`}
                  placeholder="Select a symbol"
                  className={`w-full bg-transparent border border-gray-300 rounded-lg ${assetData.symbol ? "asset_Dropdown" : ""}`}
                />
                {errors.symbol && <p className="text-red-500">{errors.symbol}</p>}
              </div>
              <div className="w-full">
                <label className="block mb-2 text-lg text-white font-semibold">Cash Assets</label>
                <Dropdown
                  value={assetData.cashAssets}
                  showClear
                  name="cashAssets"
                  onChange={handleInputChange}
                  options={userAssets}
                  optionLabel="name"
                  placeholder="Select a cash assets"
                  className={`w-full bg-transparent border border-gray-300 rounded-lg ${assetData.cashAssets ? "asset_Dropdown" : ""}`}
                />
              </div>
            </>
          )}
        </div>

        {assetType.name !== 'Luxury Asset' && <h2 className="text-2xl font-semibold mb-6 text-secondary">Transaction</h2>}
        <div className={`grid ${assetType.name === 'Stock' ? 'md:grid-cols-3 grid-cols-1' : 'md:grid-cols-2 grid-cols-1'} gap-5 text-primary mb-8`}>
          {assetType.name !== 'Luxury Asset' && (<div className='flex flex-col justify-end'>
            <div className="w-full flex items-end space-x-4">
              <label className="block text-sm text-white font-semibold">Date</label>

              <Calendar
                value={assetData.date}
                onChange={(e) => handleInputChange(e.value, 'date')}
                dateFormat="dd/mm/yy"
                className="asset_date w-full"
                maxDate={new Date()}
              />
            </div>
            {errors.date && <p className="text-red-500 text-center">{errors.date}</p>}
          </div>)}

          {assetType.name !== 'Stock' && assetType.name !== 'Luxury Asset' && (
            <>
              <InputFieldWithOutBorder
                label={assetType.name == 'Real Estate' ? "Initial Purchase" : "Initial Balance"}
                placeholder="Enter your initial balance"
                name="initialBalance"
                value={assetData.initialBalance}
                onChange={(e) => handleNumericChange(e, 'initialBalance')}
                required={false}
                errors={errors.initialBalance}
              />
            </>
          )}

          {assetType.name === 'Stock' && (
            <>
              <NumberField
                label="Quantity"
                placeholder="Enter quantity"
                name="quantity"
                value={assetData.quantity}
                onChange={handleInputChange}
                required={false}
                errors={errors.quantity}
              />

              <InputFieldWithOutBorder
                label="Cost price"
                placeholder="Enter cost price"
                name="costPrice"
                value={assetData.costPrice}
                onChange={(e) => handleNumericChange(e, 'costPrice')}
                required={false}
                errors={errors.costPrice}
              />
            </>
          )}

          {assetType.name === 'Real Estate' && (
            <>
              <NumberField
                label="NOI"
                placeholder="Enter NOI"
                name="noi"
                value={assetData.noi}
                onChange={handleInputChange}
                required={false}
                errors={errors.noi}
              />
              <NumberField
                label="Cap Rate"
                placeholder="Enter Cap Rate"
                name="capRate"
                value={assetData.capRate}
                onChange={handleInputChange}
                required={false}
                errors={errors.capRate}
              />

            </>
          )}

          {assetType.name === 'Company' && (
            <>
              <InputFieldWithOutBorder
                label="EBITDA"
                placeholder="Enter EBITDA"
                name="ebitda"
                value={assetData.ebitda}
                onChange={(e) => handleNumericChange(e, 'ebitda')}
                required={false}
                errors={errors.ebitda}
              />

              <InputFieldWithOutBorder
                label="Multiples"
                placeholder="Enter Multiples"
                name="multiples"
                value={assetData.multiples}
                onChange={(e) => handleNumericChange(e, 'multiples')}
                required={false}
                errors={errors.multiples}
              />

            </>
          )}
        </div>
        <div className='flex md:justify-end justify-center'>
          <button
            type="submit"
            className="px-6 py-2 text-primary bg-secondary hover:bg-secondary rounded-lg"
          >
            Submit
          </button>
        </div>
      </form>
    </div>
  );
};

export default AssetForm;
